import { cognitoCustomAttributes as cca, UserEnrichments } from 'utils/constants';
import { activities, corporateForms } from 'utils/formData';
import { convertToBoolOrNull } from 'utils/helpers';

export const signUp = ({
    email,
    password,
    firstName,
    lastName,
    roleName,
    roleId,
    fundId,
    fundName,
    title,
    sgpName,
    sgpLogo,
}) => ({
    // all values sent to Cognito should be strings
    password,
    username: email.toLowerCase(),
    options: {
        userAttributes: {
            email: email.toLowerCase(),
            family_name: lastName,
            given_name: firstName,
            [cca.ROLE]: roleName,
            [cca.ROLE_ID]: roleId,
            [cca.FUND_ID]: fundId,
            [cca.FUND_NAME]: fundName,
            [cca.USER_TITLE]: title,
            ...(sgpName && { [cca.SGP_NAME]: sgpName }),
            ...(sgpLogo && { [cca.SGP_LOGO]: sgpLogo }),
        },
    },
});

export const companyRegister = ({ firstName, lastName, email, roleId, ...rest }) => ({
    firstName,
    lastName: lastName.toUpperCase(),
    email: email.toLowerCase(),
    role: `/api/roles/${roleId}`,
    title: rest.title,
    birthName: rest.birthName,
    investorCompany: {
        addressLocation: {
            country: rest.currentAddress.country,
            address: rest.currentAddress.address,
            zipCode: rest.zipCode,
            city: rest.city,
            formattedAddress: rest.currentAddress.formattedAddress,
        },
        activity: JSON.stringify({
            choice: rest.activity,
            ...(rest.activity === activities.other && { other: rest.otherActivity }),
        }),
        corporateName: rest.corporateName,
        corporateForm: JSON.stringify({
            choice: rest.corporateForm,
            ...(rest.corporateForm === corporateForms.other && { other: rest.otherCorporateForm }),
        }),
        shareCapital: +rest.shareCapital,
        currency: rest.currency,
        siret: rest.siret,
        rcsNumber: rest.rcsNumber,
        taxSystem: rest.taxSystem,
        ...(rest.signatoryOnKbis && {
            signatoryOnKbis: convertToBoolOrNull(rest.signatoryOnKbis, 'yes', 'no'),
        }),
        representativeRole: rest.representativeRole,
        representativeName: rest.representativeName,
        ...(rest.ownAccountAgreement && {
            ownAccountAgreement: convertToBoolOrNull(rest.ownAccountAgreement, 'yes', 'no'),
        }),
    },
    investorProfile: {
        termsAgreement: rest.conditions.termsAgreement,
        emailAgreement: rest.conditions.acceptedEmails,
    },
    subscriptions: [
        {
            subscriptionLabels: rest.label ? [`/api/subscription_labels/${rest.label}`] : [],
            role: `/api/roles/${roleId}`,
            investmentFund: `/api/investment_funds/${rest.fundId}`,
            legalRepresentativeDocumentFile: rest.legalRepresentativeDocumentFile,
            legalRepresentativeDocumentFileExtension: rest.legalRepresentativeDocumentFileExtension,
            ...(rest.subcategoryId && {
                investmentCharacteristics: {
                    shareSubcategory: `/api/share_subcategories/${rest.subcategoryId}`,
                },
            }),
            ...(rest.referralEmail && {
                referralEmail: rest.referralEmail,
            }),
            ...(rest.externalDistributorEmail && {
                externalDistributorEmail: rest.externalDistributorEmail,
            }),
        },
    ],
});

export const distributorRegister = ({ firstName, lastName, email, title, roleId, ...rest }) => ({
    firstName,
    lastName: lastName.toUpperCase(),
    email: email.toLowerCase(),
    title,
    role: `/api/roles/${roleId}`,
    phoneNumber: rest.phoneNumber,
    distributorCompany: {
        currentLocation: {
            city: rest.currentAddress.city,
            zipCode: rest.currentAddress.zipCode,
            address: rest.currentAddress.address,
            country: rest.currentAddress.country,
            formattedAddress: rest.currentAddress.formattedAddress,
        },
        corporateName: rest.corporateName,
        organization: rest.organization === 'true',
        corporateForm: JSON.stringify({
            choice: rest.corporateForm,
            ...(rest.corporateForm === corporateForms.other && { other: rest.otherCorporateForm }),
        }),
        shareCapital: +rest.shareCapital,
        representativeRole: rest.representativeRole,
        representativeName: rest.representativeName,
        legalRepresentant: rest.legalRepresentant === 'yes',
        function: rest.function,
    },
    investorProfile: {
        termsAgreement: rest.conditions.termsAgreement,
        emailAgreement: rest.conditions.acceptedEmails,
    },
    subscriptions: [
        {
            subscriptionLabels: rest.label ? [`/api/subscription_labels/${rest.label}`] : [],
            role: `/api/roles/${roleId}`,
            ...(rest.subcategory && {
                investmentCharacteristics: {
                    shareSubcategory: `/api/share_subcategories/${rest.subcategory}`,
                },
            }),
        },
    ],
});

export const updateUserAttributes = ({ firstName, lastName, fundName, title }) => ({
    family_name: lastName,
    given_name: firstName,
    [cca.FUND_NAME]: fundName,
    [cca.USER_TITLE]: title,
});

export const register = ({ firstName, lastName, email, roleId, ...rest }) => ({
    birthName: rest.birthName,
    title: rest.title,
    firstName,
    lastName: lastName.toUpperCase(),
    email: email.toLowerCase(),
    investorProfile: {
        termsAgreement: rest.conditions.termsAgreement,
        emailAgreement: rest.conditions.acceptedEmails,
    },
    subscriptions: [
        {
            subscriptionLabels: rest.label ? [`/api/subscription_labels/${rest.label}`] : [],
            role: `/api/roles/${roleId}`,
            investmentFund: `/api/investment_funds/${rest.fundId}`,
            ...(rest.subcategoryId && {
                investmentCharacteristics: {
                    shareSubcategory: `/api/share_subcategories/${rest.subcategoryId}`,
                },
            }),
            ...(rest.referralEmail && {
                referralEmail: rest.referralEmail,
            }),
            ...(rest.externalDistributorEmail && {
                externalDistributorEmail: rest.externalDistributorEmail,
            }),
        },
    ],
});

export const getInvestorNecessaryEnrichments = () => ({
    enrich_with: [
        UserEnrichments.ALERTS,
        UserEnrichments.BANKING_STATEMENT,
        UserEnrichments.COMMENTS,
        UserEnrichments.COMPANY,
        UserEnrichments.CREATED_BY,
        UserEnrichments.DISTRIBUTOR_COMPANY,
        UserEnrichments.DOCUMENTS,
        UserEnrichments.INVESTMENT_CHARACTERISTICS,
        UserEnrichments.INVESTMENT_FUND,
        UserEnrichments.INVESTOR_PROFILE,
        UserEnrichments.KYC_SURVEY,
        UserEnrichments.NETWORKS,
        UserEnrichments.NOTIFICATIONS,
        UserEnrichments.PAYMENTS,
        UserEnrichments.ROLES,
        UserEnrichments.SHARE_SUBCATEGORY,
        UserEnrichments.SHARE_CATEGORY,
        UserEnrichments.SIGNATURES,
        UserEnrichments.SUBSCRIPTIONS,
        UserEnrichments.USER_ADEQUACY,
        UserEnrichments.USER_KNOWLEDGE,
        UserEnrichments.USER_TUNNEL_STEPS,
        UserEnrichments.SGP,
    ],
});

export const getClientSpaceEnrichments = () => ({
    enrich_with: [
        ...getInvestorNecessaryEnrichments().enrich_with,
        UserEnrichments.INTERNAL_RATE_OF_RETURN,
    ],
});

export const getRoleEnrichments = () => ({
    enrich_with: [UserEnrichments.SUBSCRIPTIONS, UserEnrichments.ROLES],
});
export const getAdminEnrichments = () => ({
    enrich_with: [
        UserEnrichments.SUBSCRIPTIONS,
        UserEnrichments.INVESTMENT_FUND,
        UserEnrichments.ROLES,
        UserEnrichments.DISTRIBUTOR_COMPANY,
        UserEnrichments.DOCUMENTS,
        UserEnrichments.CREATED_BY,
        UserEnrichments.NETWORKS,
        UserEnrichments.SGP,
    ],
});

export const setPassword = payload => ({
    subscriptionHash: payload.subscriptionHash,
    password: payload.password,
});
